import { isAnyOf } from '@reduxjs/toolkit';
import { isNotDefined } from '@sgme/fp';

import { getSignalRConnector } from '@/api/signalR/SignalRConnector.ts';
import type { StartAppListening } from '@/store/listenerMiddleware.ts';
import type { AppDispatch, AppState } from '@/store/store.ts';
import { swapApi } from '@/store/swap/api';
import { selectSwapTile } from '@/store/swap/selectors/tile.ts';
import { swapHeartbeatTimeout, swapSlice } from '@/store/swap/slice.ts';
import { tilesSlice } from '@/store/tiles/slice.ts';

const heartbeatListenersByRfsId: Record<string, (rfsId: string) => void> = {};

export const registerRfsListenersForSwap = (startAppListening: StartAppListening) => {
  startAppListening({
    matcher: swapApi.endpoints.requestSwapRfs.matchFulfilled,
    effect: async (action, api) => {
      const { rfsId, tileId } = action.meta.arg.originalArgs;
      const signalRConnector = getSignalRConnector();

      function onHeartbeatTimeout(id: string) {
        if (id !== rfsId) {
          return;
        }

        api.dispatch(swapHeartbeatTimeout({ tileId }));
        api.dispatch(swapApi.endpoints.cancelSwapRfs.initiate({ tileId, rfsId }));
      }

      heartbeatListenersByRfsId[rfsId] = onHeartbeatTimeout;
      signalRConnector.onHeartbeatTimeout(rfsId, onHeartbeatTimeout);
    },
  });

  startAppListening({
    matcher: swapApi.endpoints.executeSwapRfs.matchPending,
    effect: async (action) => {
      const { rfsId } = action.meta.arg.originalArgs;
      const listener = heartbeatListenersByRfsId[rfsId];
      if (listener) {
        getSignalRConnector().offHeartbeatTimeout(rfsId, listener);
        delete heartbeatListenersByRfsId[rfsId];
      }
    },
  });

  startAppListening({
    matcher: swapApi.endpoints.cancelSwapRfs.matchPending,
    effect: async (action) => {
      const { rfsId } = action.meta.arg.originalArgs;

      const listener = heartbeatListenersByRfsId[rfsId];
      if (listener) {
        getSignalRConnector().offHeartbeatTimeout(rfsId, listener);
        delete heartbeatListenersByRfsId[rfsId];
      }
    },
  });

  /**
   * This listener is triggered when trade capture is called.
   */
  startAppListening({
    matcher: swapApi.endpoints.checkSwapByTradeCapture.matchFulfilled,
    effect: async (action, api) => {
      const tileId = action.meta.arg.originalArgs.tileId;
      const tradeCaptureResponse = action.payload;

      if (isNotDefined(tradeCaptureResponse) || !tradeCaptureResponse.changedFields.isPriceObsolete) {
        return;
      }

      cancelSwapRfs(tileId, api.getState(), api.dispatch);
    },
  });

  /**
   * This listener is triggered when:
   * - the workspace client is changed
   * - the tile product is changed
   * - the tile is closed
   */
  startAppListening({
    matcher: isAnyOf(
      swapSlice.actions.swapClientIdUpdated.match,
      tilesSlice.actions.updateTileProductType.match,
      tilesSlice.actions.removeTile.match,
    ),
    effect: async (action, api) => {
      const state = api.getOriginalState();

      const tileId =
        swapSlice.actions.swapClientIdUpdated.match(action) || tilesSlice.actions.updateTileProductType.match(action)
          ? action.payload.tileId
          : tilesSlice.actions.removeTile.match(action)
            ? action.payload
            : undefined;

      if (isNotDefined(tileId)) {
        return;
      }

      cancelSwapRfs(tileId, state, api.dispatch);
    },
  });
};

function cancelSwapRfs(tileId: string, state: AppState, dispatch: AppDispatch) {
  const tile = selectSwapTile(state, tileId);

  if (isNotDefined(tile?.properties.rfs)) {
    return;
  }

  dispatch(
    swapApi.endpoints.cancelSwapRfs.initiate({
      rfsId: tile.properties.rfs.rfsId,
      tileId,
    }),
  );
}
